import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowDown, Loader2, CheckCircle2, Phone,
  Building2, Stethoscope, Scissors, UtensilsCrossed, Briefcase, Dumbbell, Home, BedDouble,
  Shirt, Layers, Wind, Sparkles, CalendarClock, Truck,
} from "lucide-react";
import { Reveal, Overline, MaskLine } from "@/components/motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CONTACT } from "@/data/site";
import { OFFER } from "@/data/pricing";

const INDUSTRIES = [
  { icon: Building2, title: "Hotels & Resorts", desc: "Daily linen, bedsheets, towels and guest laundry at scale — spotless, on schedule." },
  { icon: Stethoscope, title: "Hospitals & Clinics", desc: "Hygienically sanitised scrubs, sheets and uniforms with strict turnaround." },
  { icon: Scissors, title: "Salons & Spas", desc: "Towels, robes and linens returned fresh, soft and fluffed every single day." },
  { icon: UtensilsCrossed, title: "Restaurants & Banquets", desc: "Table linen, napkins and chef whites — pressed, stain-free and event-ready." },
  { icon: Briefcase, title: "Corporate Offices", desc: "Employee uniforms, curtains and upholstery on recurring managed contracts." },
  { icon: Dumbbell, title: "Gyms & Studios", desc: "High-volume towel programs with fast rotation and antibacterial treatment." },
  { icon: Home, title: "Property Managers", desc: "Whole-property refresh for apartments, villas and serviced residences." },
  { icon: BedDouble, title: "Airbnb & Homestays", desc: "Turnover-ready linen and deep-clean packages between every guest stay." },
];

const HANDLES = [
  { icon: BedDouble, title: "Linen & Bed Sheets" },
  { icon: Shirt, title: "Uniforms & Staff Wear" },
  { icon: Layers, title: "Towels & Napkins" },
  { icon: Wind, title: "Curtains & Drapes" },
  { icon: Sparkles, title: "Carpets & Upholstery" },
  { icon: CalendarClock, title: "Recurring Scheduled Pickups" },
];

const empty = { name: "", organisation: "", phone: "", email: "", industry: "", message: "" };

const Commercial = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const scrollToQuote = () => document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.organisation) {
      toast.error("Please add your name, organisation and phone.");
      return;
    }
    setLoading(true);
    const message =
      `[COMMERCIAL ENQUIRY] Organisation: ${form.organisation} | ` +
      `Industry: ${form.industry || "Not specified"} | ` +
      `Requirement: ${form.message || "-"}`;
    try {
      await api.post("/enquiries", {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        message,
      });
      setDone(true);
      toast.success("Quote request sent! Our team will reach out shortly.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Try calling us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="commercial-page">
      {/* Top bar */}
      <header className="fixed top-0 inset-x-0 z-50 bg-background/90 backdrop-blur-md border-b border-foreground/10">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="commercial-logo" className="text-foreground"><Logo size="md" /></Link>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="back-to-site" className="hidden md:flex items-center gap-2 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors">
              <ArrowLeft size={15} /> Back to site
            </Link>
            <button onClick={scrollToQuote} data-testid="header-quote-btn"
              className="group relative border border-foreground px-5 py-2.5 text-xs md:text-sm font-medium uppercase tracking-widest overflow-hidden">
              <span className="relative z-10 transition-colors duration-300 group-hover:text-background">Request a Quote</span>
              <span className="absolute inset-0 bg-foreground translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-28 md:pt-40 pb-16 md:pb-24 max-w-[1600px] mx-auto px-5 md:px-10">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}>
          <Overline>For Business — {CONTACT.area} &amp; Beyond</Overline>
        </motion.div>
        <h1 className="mt-6 font-serif font-light uppercase leading-[0.85] tracking-tighter text-[15vw] md:text-[11vw]">
          <MaskLine delay={0.25}>Commercial</MaskLine>
          <MaskLine delay={0.4}>&amp; Bulk Care</MaskLine>
        </h1>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="md:col-span-7 text-base md:text-lg font-light text-foreground/70 leading-relaxed">
            Dedicated laundry &amp; dry-cleaning contracts for hotels, hospitals, restaurants, salons and property
            managers. Scheduled pickups, guaranteed turnaround, a named account manager — and volume discounts up to 50%.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}
            className="md:col-span-5 flex md:justify-end">
            <button onClick={scrollToQuote} data-testid="hero-quote-btn"
              className="group relative bg-foreground text-background px-8 py-4 text-sm font-medium uppercase tracking-widest overflow-hidden">
              <span className="relative z-10 flex items-center gap-2 transition-colors duration-300 group-hover:text-foreground">Request a Quote <ArrowDown size={16} className="-rotate-45" /></span>
              <span className="absolute inset-0 bg-background translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 md:py-32 bg-secondary" data-testid="industries-section">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10">
          <div className="mb-14 md:mb-20 max-w-2xl">
            <Reveal><Overline>Industries We Serve</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-4xl md:text-6xl font-light tracking-tight leading-none">
                Built for volume.
              </h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-foreground/15 border border-foreground/15">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.title} delay={(i % 4) * 0.06} className="bg-secondary">
                <div className="p-7 lg:p-8 h-full min-h-[220px] flex flex-col group hover:bg-foreground hover:text-background transition-colors duration-300" data-testid={`industry-${i}`}>
                  <ind.icon size={28} className="text-foreground group-hover:text-background transition-colors duration-300" />
                  <h3 className="mt-6 font-sans text-lg font-medium">{ind.title}</h3>
                  <p className="mt-3 text-sm font-light text-foreground/75 group-hover:text-background/80 leading-relaxed transition-colors duration-300">
                    {ind.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* What we handle */}
      <section className="py-20 md:py-32 max-w-[1600px] mx-auto px-5 md:px-10" data-testid="handles-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal><Overline>What We Handle</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl font-light tracking-tight leading-none">
                Every fabric,<br />at any scale.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 flex items-center gap-3 text-sm font-medium">
                <Truck size={18} className="text-foreground" /> Free scheduled pickup &amp; delivery
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-foreground/15 border border-foreground/15">
            {HANDLES.map((h, i) => (
              <Reveal key={h.title} delay={(i % 2) * 0.05} className="bg-background">
                <div className="flex items-center gap-4 p-6 h-full" data-testid={`handle-${i}`}>
                  <h.icon size={22} className="text-foreground shrink-0" />
                  <span className="font-sans text-lg font-light">{h.title}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Volume discount tiers */}
      <section className="py-20 md:py-32 bg-foreground text-background" data-testid="tiers-section">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10">
          <div className="mb-14 md:mb-20 max-w-3xl">
            <Reveal><Overline className="text-background/50">Volume Discounts</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-4xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95]">
                The more you send,<br />the more you save.
              </h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-background/20 border border-background/20">
            {OFFER.tiers.map((t, i) => (
              <Reveal key={t.range} delay={i * 0.1} className="bg-foreground">
                <div className="p-8 lg:p-10 h-full flex flex-col justify-between min-h-[300px]" data-testid={`tier-${i}`}>
                  <div>
                    <span className="text-xs uppercase tracking-widest text-background/50">{t.label}</span>
                    <div className="mt-6 font-serif text-6xl md:text-7xl font-light text-background leading-none">{t.pct}</div>
                    <p className="mt-4 text-base font-light text-background/70">on orders</p>
                    <p className="font-sans text-lg font-medium">{t.range}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 text-sm font-light text-background/60 max-w-2xl">{OFFER.note}</p>
          </Reveal>
        </div>
      </section>

      {/* Quote form */}
      <section id="quote" className="py-20 md:py-32 max-w-[1600px] mx-auto px-5 md:px-10" data-testid="quote-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal><Overline>Get a Quote</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-4xl md:text-6xl font-light tracking-tight leading-[0.95]">
                Let's talk<br />volume.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 space-y-6">
                <a href={`tel:${CONTACT.phoneTel}`} className="flex items-center gap-3 group" data-testid="quote-phone-link">
                  <Phone size={16} className="text-foreground" />
                  <span className="font-serif text-2xl font-light group-hover:text-foreground/60 transition-colors">{CONTACT.phone}</span>
                </a>
                <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 group" data-testid="quote-whatsapp">
                  <Phone size={16} className="text-foreground" />
                  <span className="text-base font-light text-foreground/75 group-hover:text-foreground transition-colors">WhatsApp — {CONTACT.phone}</span>
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              {done ? (
                <div className="border border-foreground/15 p-10 md:p-14 flex flex-col items-start gap-5" data-testid="quote-success">
                  <CheckCircle2 size={44} className="text-foreground" />
                  <h3 className="font-serif text-3xl md:text-4xl font-light">Request received.</h3>
                  <p className="text-foreground/75 font-light max-w-md">
                    Thank you. Our commercial team will reach out shortly with a tailored quote for your business.
                  </p>
                  <button onClick={() => setDone(false)} data-testid="quote-another"
                    className="mt-2 border border-foreground px-6 py-3 text-xs uppercase tracking-widest hover:bg-foreground hover:text-background transition-colors">
                    Send Another
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="border border-foreground/15 p-8 md:p-12" data-testid="quote-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                    <label className="block">
                      <span className="text-xs uppercase tracking-widest text-foreground/50">Contact Name</span>
                      <input data-testid="quote-name" className="slip-input mt-2" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
                    </label>
                    <label className="block">
                      <span className="text-xs uppercase tracking-widest text-foreground/50">Organisation</span>
                      <input data-testid="quote-org" className="slip-input mt-2" value={form.organisation} onChange={(e) => set("organisation", e.target.value)} placeholder="Business / property name" />
                    </label>
                    <label className="block">
                      <span className="text-xs uppercase tracking-widest text-foreground/50">Phone</span>
                      <input data-testid="quote-phone" className="slip-input mt-2" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Mobile number" />
                    </label>
                    <label className="block">
                      <span className="text-xs uppercase tracking-widest text-foreground/50">Email (optional)</span>
                      <input data-testid="quote-email" className="slip-input mt-2" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@business.com" />
                    </label>
                    <div className="block md:col-span-2">
                      <span className="text-xs uppercase tracking-widest text-foreground/50">Industry</span>
                      <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
                        <SelectTrigger data-testid="quote-industry" className="mt-2 rounded-none border-0 border-b border-foreground/30 bg-transparent px-0 focus:ring-0 focus:border-foreground">
                          <SelectValue placeholder="Select your industry" />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                          {INDUSTRIES.map((ind) => (
                            <SelectItem key={ind.title} value={ind.title} className="rounded-none">{ind.title}</SelectItem>
                          ))}
                          <SelectItem value="Other" className="rounded-none">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <label className="block md:col-span-2">
                      <span className="text-xs uppercase tracking-widest text-foreground/50">Requirement / Approx. Volume</span>
                      <input data-testid="quote-message" className="slip-input mt-2" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="e.g. ~200 sheets + 300 towels daily" />
                    </label>
                  </div>
                  <button type="submit" disabled={loading} data-testid="quote-submit"
                    className="group relative mt-10 w-full bg-foreground text-background py-4 text-sm font-medium uppercase tracking-widest overflow-hidden disabled:opacity-60">
                    <span className="relative z-10 flex items-center justify-center gap-2 transition-colors duration-300 group-hover:text-foreground">
                      {loading && <Loader2 size={16} className="animate-spin" />}
                      {loading ? "Submitting…" : "Request Quote"}
                    </span>
                    <span className="absolute inset-0 bg-background translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Commercial;
